import classNames from "classnames";
import Hammer from "hammerjs";
import React, { PropsWithChildren } from "react";

interface Props {
  onDragDown: () => void;
}

const PanDown = ({ onDragDown, children }: PropsWithChildren<Props>) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [posY, setPosY] = React.useState(0);
  const [hasInit, setInit] = React.useState(false);
  const [isDragging, setDragging] = React.useState(false);

  const handlePan = React.useCallback(
    (input: HammerInput, elem: HTMLDivElement) => {
      const shouldClose = input.deltaY / window.innerHeight > 0.25;

      if (!elem.classList.contains("lever")) {
        return;
      }

      if (!isDragging) {
        setDragging(true);
      }

      if (input.deltaY >= 0) {
        setPosY(input.deltaY);
      }

      if (input.isFinal) {
        setDragging(false);

        if (shouldClose) {
          setPosY(window.innerHeight);
          setTimeout(onDragDown, 250);
        } else {
          setPosY(0);
        }
      }
    },
    [isDragging, onDragDown]
  );

  const initHammer = React.useCallback(
    (elem: HTMLDivElement) => {
      const mc = new Hammer.Manager(elem);
      mc.add(new Hammer.Pan({ direction: Hammer.DIRECTION_DOWN, threshold: 0, pointers: 0 }));
      mc.on("pan", (input) => handlePan(input, elem));

      setInit(true);
    },
    [handlePan]
  );

  React.useEffect(() => {
    if (ref.current !== null && !hasInit) {
      const levers = ref.current.querySelectorAll<HTMLDivElement>(".lever");

      levers.forEach(initHammer);
    }
  }, [ref, hasInit, initHammer]);

  return (
    <div
      className={classNames("PanDown", { animate: !isDragging })}
      ref={ref}
      style={{
        transform: `translate3d(0, ${posY}px, 0)`,
      }}
    >
      {children}
    </div>
  );
};

export default React.memo(PanDown);
